import {
  CheckCircle,
  Clock,
  FileText,
  TrendingUp,
  XCircle,
  Calendar,
} from "lucide-react";
import React from "react";
import { useTranslation } from "../contexts/LanguageContext";
import type { FilterType } from "../types/posts";

export interface UsePostsUtilsReturn {
  getFilterIcon: (filter: FilterType) => React.ReactNode;
  getFilterLabel: (filter: FilterType) => string;
  getFilterCount: (
    filter: FilterType,
    pendingCount: number,
    approvedCount: number,
    publishedCount: number,
    stats?: any,
  ) => number;
  getEmptyStateIcon: (filter: FilterType) => React.ReactNode;
  filterPosts: (
    posts: any[],
    platformFilter: string,
    aiGeneratedFilter: string,
  ) => any[];
}

export function usePostsUtils(): UsePostsUtilsReturn {
  const { t } = useTranslation();

  const getFilterIcon = (filter: FilterType) => {
    switch (filter) {
      case "pending":
        return <Clock className="h-4 w-4" />;
      case "approved":
        return <CheckCircle className="h-4 w-4" />;
      case "published":
        return <TrendingUp className="h-4 w-4" />;
      case "rejected":
        return <XCircle className="h-4 w-4" />;
      case "scheduled":
        return <Calendar className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getFilterLabel = (filter: FilterType) => {
    switch (filter) {
      case "pending":
        return t("Pendentes");
      case "approved":
        return t("Aprovados");
      case "published":
        return t("Publicados");
      case "rejected":
        return t("Descartados");
      case "scheduled":
        return t("Agendados");
      default:
        return t("Todos os status");
    }
  };

  const getFilterCount = (
    filter: FilterType,
    pendingCount: number,
    approvedCount: number,
    publishedCount: number,
    stats?: any,
  ) => {
    switch (filter) {
      case "pending":
        return pendingCount > 0 ? pendingCount : (stats?.pending ?? 0);
      case "approved":
        return approvedCount > 0 ? approvedCount : (stats?.approved ?? 0);
      case "published":
        return publishedCount > 0 ? publishedCount : (stats?.published ?? 0);
      case "rejected":
        return stats?.rejected ?? 0;
      case "scheduled":
        return stats?.scheduled ?? 0;
    }
  };

  const getEmptyStateIcon = (filter: FilterType) => {
    switch (filter) {
      case "pending":
        return <FileText className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400" />;
      case "approved":
        return (
          <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400" />
        );
      case "published":
        return (
          <TrendingUp className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400" />
        );
      case "rejected":
        return <XCircle className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400" />;
      case "scheduled":
        return <Calendar className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400" />;
      default:
        return <FileText className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400" />;
    }
  };

  const filterPosts = (
    posts: any[],
    platformFilter: string,
    aiGeneratedFilter: string,
  ) => {
    let filteredPosts = [...posts];

    // Filtro por plataforma
    if (platformFilter !== "all") {
      filteredPosts = filteredPosts.filter((post: any) => {
        const postPlatform = (post.platform || "").toLowerCase();
        return postPlatform === platformFilter.toLowerCase();
      });
    }

    // Filtro por gerado por IA
    if (aiGeneratedFilter !== "all") {
      filteredPosts = filteredPosts.filter((post: any) => {
        return aiGeneratedFilter === "yes"
          ? post.agent_generated === true
          : post.agent_generated !== true;
      });
    }

    // Ordenar em ordem decrescente por ID (mais recentes primeiro)
    return filteredPosts.sort((a: any, b: any) => b.id - a.id);
  };

  return {
    getFilterIcon,
    getFilterLabel,
    getFilterCount,
    getEmptyStateIcon,
    filterPosts,
  };
}
